// 实体文字人工审核台：卡牌与角色分别加载清单，共用逐行、逐段颜色校订能力。
// 原图始终完整显示；审核结果只写结构化数据，不在图片上叠加伪造文字。

import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";

import "./review.css";

type ReviewStatus = "pending" | "approved" | "rejected";
type Tone = "plain" | "key" | "value" | "custom";
type ReviewMode = "card" | "role";

interface Ink {
  tone: Tone;
  color?: string | undefined;
}

interface Run extends Ink {
  text: string;
}

type Line = Run[];

interface ScanSnapshot {
  name: string;
  text: string;
  raw: string[];
}

interface ReviewCard extends Record<string, unknown> {
  id: string;
  deck: string;
  camp: string;
  category: string;
  copies: number;
  hp: number | null;
  hp_seed: number | null;
  asset: string;
  source: string;
  evidence: string;
  scan: ScanSnapshot;
  backend: Record<string, unknown>;
  seed: Line[];
  name_seed: Line;
  name_runs: Line;
  name: string;
  lines: Line[];
  text: string;
  review: { status: ReviewStatus; notes: string };
}

interface ReviewDocument extends Record<string, unknown> {
  schema_version: number;
  items: ReviewCard[];
}

interface ReviewConfig {
  mode: ReviewMode;
  collection: "cards" | "roles";
  dataUrl: string;
  storageKey: string;
  exportPrefix: string;
  heading: string;
  eyebrow: string;
  description: string;
  unit: string;
  itemLabel: string;
  groups: readonly { value: string; label: string }[];
}

interface TextSelection {
  target: string;
  start: number;
  end: number;
}

const SCHEMA_VERSION = 3;
const MIN_SCHEMA_VERSION = 2;
const NAME_TARGET = "name";
const bodyTarget = (index: number): string => `body:${index}`;

const GROUP_LABEL: Record<string, string> = {
  hakurei: "博丽神社",
  moriya: "守矢神社",
  kleinji: "命莲寺",
  jiki: "自机",
  boss: "BOSS",
  yajiuma: "野次马",
};

const REVIEW_CONFIG: Record<ReviewMode, ReviewConfig> = {
  card: {
    mode: "card",
    collection: "cards",
    dataUrl: "/card-review.json",
    storageKey: "touranki.card-review.v1",
    exportPrefix: "touranki-card-review",
    heading: "卡牌文字审核台",
    eyebrow: "TOURANKI CARD TEXT REVIEW",
    description: "原图只作对照，不叠字。校订名称、正文、换行及任意文字颜色；导出的 name 与 text 才是游戏数据。",
    unit: "张卡",
    itemLabel: "卡牌",
    groups: [
      { value: "hakurei", label: "博丽神社" },
      { value: "moriya", label: "守矢神社" },
      { value: "kleinji", label: "命莲寺" },
    ],
  },
  role: {
    mode: "role",
    collection: "roles",
    dataUrl: "/role-review.json",
    storageKey: "touranki.role-review.v1",
    exportPrefix: "touranki-role-review",
    heading: "角色技能审核台",
    eyebrow: "TOURANKI ROLE RULE REVIEW",
    description: "以完整实体角色卡为准，人工校订角色名、HP、胜利条件、公开条件和全部特殊能力；当前后端登记只作差异对照。",
    unit: "名角色",
    itemLabel: "角色",
    groups: [
      { value: "jiki", label: "自机" },
      { value: "boss", label: "BOSS" },
      { value: "yajiuma", label: "野次马" },
    ],
  },
};

const CATEGORY_LABEL: Record<string, string> = {
  equipment: "装备",
  once: "使用后舍弃",
  kleinji: "命莲寺",
  unknown: "未分类",
};

const STATUS_LABEL: Record<ReviewStatus, string> = {
  pending: "待审核",
  approved: "已通过",
  rejected: "需重审",
};

const TONE_LABEL: Record<Tone, string> = {
  plain: "正文黑",
  key: "条件黄",
  value: "数值红",
  custom: "自定义色",
};

const KEY_BY_DIGIT: Record<string, Exclude<Tone, "custom">> = {
  "1": "plain",
  "2": "key",
  "3": "value",
};

const COLOR_PRESETS: readonly {
  label: string;
  ink: Ink;
  shortcut?: string | undefined;
}[] = [
  { label: "正文黑", ink: { tone: "plain" }, shortcut: "1" },
  { label: "条件黄", ink: { tone: "key" }, shortcut: "2" },
  { label: "数值红", ink: { tone: "value" }, shortcut: "3" },
  { label: "紫", ink: { tone: "custom", color: "#8759c7" } },
  { label: "绿", ink: { tone: "custom", color: "#2f9b67" } },
];

function record(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function oneOf<T extends string>(value: unknown, options: readonly T[], fallback: T): T {
  return typeof value === "string" && options.includes(value as T) ? (value as T) : fallback;
}
function positiveInteger(value: unknown): number | null {
  if (typeof value !== "number" && typeof value !== "string") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function reviewGroup(card: ReviewCard, mode: ReviewMode): string {
  return mode === "role" ? card.camp : card.deck;
}



function compactLine(line: Line): Line {
  const compacted: Line = [];
  for (const run of line) {
    if (!run.text) continue;
    const last = compacted[compacted.length - 1];
    if (last?.tone === run.tone && last.color === run.color) last.text += run.text;
    else compacted.push({ ...run });
  }
  return compacted.length > 0 ? compacted : [{ text: "", tone: "plain" }];
}

function normalizedLine(value: unknown): Line | null {
  if (!Array.isArray(value)) return null;
  const runs = value.map((entry) => {
    const input = record(entry);
    const tone = oneOf(
      input["tone"],
      ["plain", "key", "value", "custom"] as const,
      "plain",
    );
    const candidate = text(input["color"]);
    const color = /^#[0-9a-f]{6}$/i.test(candidate) ? candidate.toLowerCase() : "#8759c7";
    return {
      text: text(input["text"]),
      tone,
      ...(tone === "custom" ? { color } : {}),
    };
  });
  return runs.length > 0 ? compactLine(runs) : [{ text: "", tone: "plain" }];
}

function normalizedLines(value: unknown): Line[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const lines = value.map(normalizedLine);
  return lines.every((line): line is Line => line !== null) ? lines : null;
}

function cloneLine(line: Line): Line {
  return line.map((run) => ({ ...run }));
}

function cloneLines(lines: Line[]): Line[] {
  return lines.map(cloneLine);
}

function lineText(line: Line): string {
  return line.map((run) => run.text).join("");
}

function bodyText(lines: Line[]): string {
  return lines.map(lineText).join("\n");
}

function seedRuns(value: string): Line {
  return [{ text: value, tone: "plain" }];
}

function cardEdited(card: ReviewCard, mode: ReviewMode): boolean {
  return (
    JSON.stringify(card.lines) !== JSON.stringify(card.seed) ||
    JSON.stringify(card.name_runs) !== JSON.stringify(card.name_seed) ||
    (mode === "role" && card.hp !== card.hp_seed)
  );
}

function inkUnits(line: Line): Ink[] {
  const units: Ink[] = [];
  for (const run of line) {
    for (let index = 0; index < run.text.length; index += 1) {
      units.push({ tone: run.tone, ...(run.color ? { color: run.color } : {}) });
    }
  }
  return units;
}

function lineFromUnits(value: string, inks: Ink[]): Line {
  if (!value) return [{ text: "", tone: "plain" }];
  const runs: Line = [];
  for (let index = 0; index < value.length; index += 1) {
    const ink = inks[index] ?? { tone: "plain" };
    const character = value[index] ?? "";
    const last = runs[runs.length - 1];
    if (last?.tone === ink.tone && last.color === ink.color) last.text += character;
    else runs.push({ text: character, ...ink });
  }
  return compactLine(runs);
}

/** 修改整行文字时保留未变区域的颜色；新输入文字继承光标左侧颜色。 */
function replaceLineText(line: Line, nextValue: string): Line {
  const currentValue = lineText(line);
  if (currentValue === nextValue) return cloneLine(line);

  let prefix = 0;
  while (
    prefix < currentValue.length &&
    prefix < nextValue.length &&
    currentValue[prefix] === nextValue[prefix]
  ) {
    prefix += 1;
  }

  let suffix = 0;
  while (
    suffix < currentValue.length - prefix &&
    suffix < nextValue.length - prefix &&
    currentValue[currentValue.length - 1 - suffix] === nextValue[nextValue.length - 1 - suffix]
  ) {
    suffix += 1;
  }

  const currentInks = inkUnits(line);
  const insertedLength = nextValue.length - prefix - suffix;
  const inheritedInk = currentInks[prefix - 1] ?? currentInks[prefix] ?? { tone: "plain" };
  const nextInks = [
    ...currentInks.slice(0, prefix),
    ...Array.from({ length: Math.max(0, insertedLength) }, () => ({ ...inheritedInk })),
    ...currentInks.slice(currentValue.length - suffix),
  ];
  return lineFromUnits(nextValue, nextInks);
}

function paintLine(line: Line, start: number, end: number, ink: Ink): Line {
  const value = lineText(line);
  const from = Math.max(0, Math.min(start, end, value.length));
  const to = Math.max(from, Math.min(Math.max(start, end), value.length));
  if (from === to) return cloneLine(line);
  const inks = inkUnits(line);
  for (let index = from; index < to; index += 1) inks[index] = { ...ink };
  return lineFromUnits(value, inks);
}

function sliceLine(line: Line, start: number, end: number): Line {
  const value = lineText(line);
  return lineFromUnits(value.slice(start, end), inkUnits(line).slice(start, end));
}

function splitLine(lines: Line[], lineIndex: number, start: number, end: number): Line[] {
  const line = lines[lineIndex];
  if (!line) return lines;
  const length = lineText(line).length;
  const from = Math.max(0, Math.min(start, end, length));
  const to = Math.max(from, Math.min(Math.max(start, end), length));
  const next = cloneLines(lines);
  next.splice(lineIndex, 1, sliceLine(line, 0, from), sliceLine(line, to, length));
  return next;
}

function mergeLineUp(lines: Line[], lineIndex: number): Line[] {
  const previous = lines[lineIndex - 1];
  const current = lines[lineIndex];
  if (!previous || !current) return lines;
  const next = cloneLines(lines);
  next.splice(lineIndex - 1, 2, compactLine([...cloneLine(previous), ...cloneLine(current)]));
  return next;
}

function insertLine(lines: Line[], lineIndex: number): Line[] {
  const next = cloneLines(lines);
  next.splice(lineIndex + 1, 0, [{ text: "", tone: "plain" }]);
  return next;
}

function removeLine(lines: Line[], lineIndex: number): Line[] {
  if (lines.length === 1) return [[{ text: "", tone: "plain" }]];
  return lines.filter((_, index) => index !== lineIndex).map(cloneLine);
}

function normalizeScan(value: unknown): ScanSnapshot {
  const input = record(value);
  return {
    name: text(input["name"]),
    text: text(input["text"]),
    raw: Array.isArray(input["raw"]) ? input["raw"].map((line) => text(line)) : [],
  };
}

function normalizeCard(value: unknown, index: number, mode: ReviewMode): ReviewCard {
  const input = record(value);
  const id = text(input["id"]).trim();
  const itemLabel = REVIEW_CONFIG[mode].itemLabel;
  if (!id) throw new Error(`第 ${index + 1} 个${itemLabel}缺少 id`);

  const scan = normalizeScan(input["scan"]);
  const legacyText = text(input["text"]);
  const lines =
    normalizedLines(input["lines"]) ??
    (legacyText
      ? legacyText.split("\n").map((line) => [{ text: line, tone: "plain" as Tone }])
      : [[{ text: "", tone: "plain" as const }]]);
  const name = text(input["name"], scan.name || id);
  const nameRuns = normalizedLine(input["name_runs"]) ?? seedRuns(name);
  const review = record(input["review"]);
  const hpSeed = positiveInteger(input["hp_seed"]) ?? positiveInteger(input["hp"]);

  return {
    ...input,
    id,
    deck: text(input["deck"]),
    camp: text(input["camp"]),
    category: text(input["category"], text(input["kind"], "unknown")),
    copies: Math.max(1, Math.round(Number(input["copies"]) || 1)),
    hp: positiveInteger(input["hp"]) ?? hpSeed,
    hp_seed: hpSeed,
    asset: text(input["asset"], `/cards/${id}.webp`),
    source: text(input["source"]),
    evidence: text(input["evidence"]),
    scan,
    backend: record(input["backend"]),
    seed: normalizedLines(input["seed"]) ?? cloneLines(lines),
    name_seed: normalizedLine(input["name_seed"]) ?? cloneLine(nameRuns),
    name_runs: nameRuns,
    name: lineText(nameRuns),
    lines,
    text: bodyText(lines),
    review: {
      status: oneOf(review["status"], ["pending", "approved", "rejected"] as const, "pending"),
      notes: text(review["notes"]),
    },
  };
}

function normalizeDocument(value: unknown, mode: ReviewMode): ReviewDocument {
  const config = REVIEW_CONFIG[mode];
  const input =
    Array.isArray(value) && mode === "card"
      ? { schema_version: SCHEMA_VERSION, cards: value }
      : record(value);
  const values = input[config.collection];
  if (!Array.isArray(values) || values.length === 0) {
    throw new Error(`JSON 必须包含非空 ${config.collection} 数组`);
  }
  if (
    mode === "role" &&
    input["document_type"] !== undefined &&
    input["document_type"] !== "roles"
  ) {
    throw new Error("这不是角色审核 JSON");
  }
  const items = values.map((item, index) => normalizeCard(item, index, mode));
  const ids = new Set<string>();
  for (const item of items) {
    if (ids.has(item.id)) throw new Error(`${config.itemLabel} id 重复：${item.id}`);
    ids.add(item.id);
  }
  const metadata = { ...input };
  delete metadata["cards"];
  delete metadata["roles"];
  return { ...metadata, schema_version: SCHEMA_VERSION, items };
}

function serializedCard(card: ReviewCard, mode: ReviewMode): Record<string, unknown> {
  if (mode === "role") {
    const { deck: _deck, category: _category, copies: _copies, ...role } = card;
    return {
      ...role,
      name: lineText(card.name_runs),
      text: bodyText(card.lines),
    };
  }
  const {
    camp: _camp,
    hp: _hp,
    hp_seed: _hpSeed,
    backend: _backend,
    ...entry
  } = card;
  return {
    ...entry,
    name: lineText(card.name_runs),
    text: bodyText(card.lines),
  };
}

function serializeDocument(sheet: ReviewDocument, mode: ReviewMode): Record<string, unknown> {
  const config = REVIEW_CONFIG[mode];
  const { items, ...metadata } = sheet;
  return {
    ...metadata,
    schema_version: SCHEMA_VERSION,
    [config.collection]: items.map((item) => serializedCard(item, mode)),
  };
}

function parseDocument(source: string, mode: ReviewMode): ReviewDocument {
  try {
    return normalizeDocument(JSON.parse(source) as unknown, mode);
  } catch (error) {
    if (error instanceof SyntaxError) throw new Error(`JSON 语法错误：${error.message}`);
    throw error;
  }
}

function ToneText({ line, empty = "（空）" }: { line: Line; empty?: string }) {
  const value = lineText(line);
  if (!value) return <span className="tone-empty">{empty}</span>;
  return (
    <>
      {line.map((run, index) => (
        <span
          className={`text-tone text-tone-${run.tone}`}
          style={run.tone === "custom" ? { color: run.color } : undefined}
          key={`${index}-${run.tone}-${run.color ?? ""}`}
        >
          {run.text}
        </span>
      ))}
    </>
  );
}

function ToneLegend() {
  return (
    <span className="tone-legend" aria-label="文字颜色说明">
      <span className="legend-plain">黑</span>
      <span className="legend-key">黄</span>
      <span className="legend-value">红</span>
      <span className="legend-purple">紫</span>
      <span className="legend-green">绿</span>
      <span>及自定义色</span>
    </span>
  );
}
function BackendSummary({ value }: { value: Record<string, unknown> }) {
  const win = record(value["win_condition"]);
  const reveal = record(value["reveal_rule"]);
  const abilities = Array.isArray(value["abilities"])
    ? value["abilities"].map(record)
    : [];
  const camp = text(value["camp"]);
  return (
    <dl className="backend-snapshot">
      <dt>角色</dt>
      <dd>{text(value["name"], "未登记")}</dd>
      <dt>HP</dt>
      <dd>{positiveInteger(value["hp"]) ?? "未登记"}</dd>
      <dt>阵营</dt>
      <dd>{GROUP_LABEL[camp] ?? (camp || "未登记")}</dd>
      <dt>胜利条件</dt>
      <dd>
        <code>{text(win["id"], "未登记")}</code>
        <span>{text(win["summary"])}</span>
      </dd>
      <dt>公开限制</dt>
      <dd>
        {Object.keys(reveal).length ? (
          <>
            <code>{text(reveal["id"], "未登记")}</code>
            <span>{text(reveal["summary"])}</span>
          </>
        ) : (
          "无登记"
        )}
      </dd>
      <dt>能力登记</dt>
      <dd>
        {abilities.length ? (
          <ul>
            {abilities.map((ability) => (
              <li key={text(ability["id"], text(ability["symbol"]))}>
                <code>{text(ability["id"], text(ability["symbol"]))}</code>
                <span>{text(ability["summary"])}</span>
              </li>
            ))}
          </ul>
        ) : (
          "无登记"
        )}
      </dd>
    </dl>
  );
}


function TextLineEditor({
  line,
  number,
  label,
  inputRef,
  onChange,
  onSelect,
  onKeyDown,
  onInsert,
  onRemove,
}: {
  line: Line;
  number: string;
  label: string;
  inputRef: (node: HTMLInputElement | null) => void;
  onChange: (value: string, input: HTMLInputElement) => void;
  onSelect: (input: HTMLInputElement) => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLInputElement>) => void;
  onInsert?: (() => void) | undefined;
  onRemove?: (() => void) | undefined;
}) {
  return (
    <div className="transcript-line">
      <span className="transcript-number">{number}</span>
      <div className="transcript-content">
        <input
          ref={inputRef}
          type="text"
          value={lineText(line)}
          aria-label={label}
          spellCheck={false}
          onChange={(event) => onChange(event.currentTarget.value, event.currentTarget)}
          onFocus={(event) => onSelect(event.currentTarget)}
          onSelect={(event) => onSelect(event.currentTarget)}
          onKeyDown={onKeyDown}
        />
        <div className="transcript-color-preview" aria-label={`${label}的颜色预览`}>
          <ToneText line={line} />
        </div>
      </div>
      {(onInsert || onRemove) && (
        <div className="transcript-actions">
          {onInsert && (
            <button type="button" onClick={onInsert} title="在下方插入一行" aria-label="在下方插入一行">
              ＋
            </button>
          )}
          {onRemove && (
            <button type="button" onClick={onRemove} title="删除这一行" aria-label="删除这一行">
              ×
            </button>
          )}
        </div>
      )}
    </div>
  );
}


function ReviewApp({ mode }: { mode: ReviewMode }) {
  const config = REVIEW_CONFIG[mode];
  const [sheet, setSheet] = useState<ReviewDocument | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [changedOnly, setChangedOnly] = useState(false);
  const [imageZoomed, setImageZoomed] = useState(false);
  const [selection, setSelection] = useState<TextSelection | null>(null);
  const [customColor, setCustomColor] = useState("#4d8fd6");
  const [message, setMessage] = useState("正在载入审核清单…");
  const fileRef = useRef<HTMLInputElement>(null);
  const inputRefs = useRef(new Map<string, HTMLInputElement>());

  useEffect(() => {
    const stored = localStorage.getItem(config.storageKey);
    if (stored) {
      try {
        const parsed = record(JSON.parse(stored) as unknown);
        if (Number(parsed["schema_version"]) >= MIN_SCHEMA_VERSION) {
          const restored = normalizeDocument(parsed, mode);
          setSheet(restored);
          setSelectedId(restored.items[0]?.id ?? "");
          setMessage("已恢复本机草稿");
          return;
        }
        localStorage.removeItem(config.storageKey);
      } catch {
        localStorage.removeItem(config.storageKey);
      }
    }

    const controller = new AbortController();
    void fetch(config.dataUrl, { cache: "no-store", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`载入失败：HTTP ${response.status}`);
        return parseDocument(await response.text(), mode);
      })
      .then((loaded) => {
        setSheet(loaded);
        setSelectedId(loaded.items[0]?.id ?? "");
        setMessage(`已载入 ${loaded.items.length} ${config.unit}`);
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setMessage(error instanceof Error ? error.message : "无法载入审核清单");
      });
    return () => controller.abort();
  }, [config, mode]);

  useEffect(() => {
    if (sheet) {
      localStorage.setItem(config.storageKey, JSON.stringify(serializeDocument(sheet, mode)));
    }
  }, [config.storageKey, mode, sheet]);

  const active = sheet?.items.find((card) => card.id === selectedId) ?? sheet?.items[0] ?? null;

  const editedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const card of sheet?.items ?? []) {
      if (cardEdited(card, mode)) ids.add(card.id);
    }
    return ids;
  }, [mode, sheet]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    return (sheet?.items ?? []).filter(
      (card) =>
        (groupFilter === "all" || reviewGroup(card, mode) === groupFilter) &&
        (statusFilter === "all" || card.review.status === statusFilter) &&
        (!changedOnly || editedIds.has(card.id)) &&
        (!needle ||
          `${card.name} ${card.id} ${bodyText(card.lines)} ${card.scan.name} ${card.scan.text}`
            .toLocaleLowerCase()
            .includes(needle)),
    );
  }, [changedOnly, editedIds, groupFilter, mode, query, sheet, statusFilter]);

  const counts = useMemo(() => {
    const result: Record<ReviewStatus, number> = { pending: 0, approved: 0, rejected: 0 };
    for (const card of sheet?.items ?? []) result[card.review.status] += 1;
    return result;
  }, [sheet]);

  const patchCard = (change: (card: ReviewCard) => ReviewCard): void => {
    if (!active) return;
    setSheet((current) =>
      current
        ? {
            ...current,
            items: current.items.map((card) => (card.id === active.id ? change(card) : card)),
          }
        : current,
    );
  };

  const patchLines = (change: (lines: Line[]) => Line[]): void => {
    patchCard((card) => {
      const lines = change(cloneLines(card.lines));
      return { ...card, lines, text: bodyText(lines) };
    });
  };

  const patchTarget = (target: string, change: (line: Line) => Line): void => {
    patchCard((card) => {
      if (target === NAME_TARGET) {
        const nameRuns = compactLine(change(cloneLine(card.name_runs)));
        return { ...card, name_runs: nameRuns, name: lineText(nameRuns) };
      }
      const index = Number(target.slice(5));
      const current = card.lines[index];
      if (!current) return card;
      const lines = cloneLines(card.lines);
      lines[index] = compactLine(change(cloneLine(current)));
      return { ...card, lines, text: bodyText(lines) };
    });
  };

  const rememberSelection = (target: string, input: HTMLInputElement): TextSelection => {
    const next = {
      target,
      start: input.selectionStart ?? 0,
      end: input.selectionEnd ?? input.selectionStart ?? 0,
    };
    setSelection(next);
    return next;
  };

  const focusLater = (target: string, start: number, end = start): void => {
    window.requestAnimationFrame(() => {
      const input = inputRefs.current.get(target);
      if (!input) return;
      input.focus();
      input.setSelectionRange(start, end);
      setSelection({ target, start, end });
    });
  };

  const paintSelection = (
    current: TextSelection | null,
    ink: Ink,
    label: string,
  ): void => {
    if (!current || current.start === current.end) {
      setMessage("先在名称或正文输入框中选中要标色的文字");
      return;
    }
    patchTarget(current.target, (line) => paintLine(line, current.start, current.end, ink));
    setMessage(`已把选中的 ${Math.abs(current.end - current.start)} 个字标为${label}`);
    focusLater(current.target, current.start, current.end);
  };

  const handleKeyDown = (
    event: ReactKeyboardEvent<HTMLInputElement>,
    target: string,
    lineIndex?: number,
  ): void => {
    const input = event.currentTarget;
    const current = {
      target,
      start: input.selectionStart ?? 0,
      end: input.selectionEnd ?? input.selectionStart ?? 0,
    };
    const shortcutTone = KEY_BY_DIGIT[event.key];
    if ((event.ctrlKey || event.metaKey) && shortcutTone) {
      event.preventDefault();
      setSelection(current);
      paintSelection(current, { tone: shortcutTone }, TONE_LABEL[shortcutTone]);
      return;
    }
    if (lineIndex === undefined) return;

    if (event.key === "Enter") {
      event.preventDefault();
      patchLines((lines) => splitLine(lines, lineIndex, current.start, current.end));
      focusLater(bodyTarget(lineIndex + 1), 0);
      return;
    }

    if (event.key === "Backspace" && current.start === 0 && current.end === 0 && lineIndex > 0) {
      event.preventDefault();
      const previousLength = active ? lineText(active.lines[lineIndex - 1] ?? []).length : 0;
      patchLines((lines) => mergeLineUp(lines, lineIndex));
      focusLater(bodyTarget(lineIndex - 1), previousLength);
    }
  };

  const selectCard = (id: string): void => {
    setSelectedId(id);
    setSelection(null);
    setImageZoomed(false);
  };

  const setReviewStatus = (status: ReviewStatus): void => {
    patchCard((card) => ({ ...card, review: { ...card.review, status } }));
  };

  const approveAndNext = (): void => {
    if (!active) return;
    const index = filtered.findIndex((card) => card.id === active.id);
    const next = index >= 0 ? filtered[index + 1] : undefined;
    setReviewStatus("approved");
    if (next) selectCard(next.id);
    setMessage(next ? `「${active.name}」已通过，已进入下一项` : `「${active.name}」已通过`);
  };

  const importFile = async (file: File): Promise<void> => {
    try {
      const loaded = parseDocument(await file.text(), mode);
      setSheet(loaded);
      selectCard(loaded.items[0]?.id ?? "");
      setMessage(`已导入 ${file.name}，共 ${loaded.items.length} ${config.unit}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "无法读取 JSON");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const exportFile = (): void => {
    if (!sheet) return;
    const payload = serializeDocument(sheet, mode);
    const blob = new Blob([`${JSON.stringify(payload, null, 2)}\n`], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${config.exportPrefix}-${new Date().toISOString().slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    setMessage(
      `已导出 ${sheet.items.length} ${config.unit}；${counts.approved} 项通过，${editedIds.size} 项有人工修改`,
    );
  };

  const discardDraft = (): void => {
    if (!window.confirm("放弃本机尚未导出的审核草稿，并重新载入初始清单？")) return;
    localStorage.removeItem(config.storageKey);
    window.location.reload();
  };

  const copyBody = (): void => {
    if (!active) return;
    void navigator.clipboard
      .writeText(bodyText(active.lines))
      .then(() => setMessage(`已复制「${active.name}」的校订正文`))
      .catch(() => setMessage("复制失败：浏览器拒绝了剪贴板写入"));
  };

  const resetActive = (): void => {
    if (!active || !cardEdited(active, mode)) return;
    if (!window.confirm(`把「${active.name}」还原为本清单的识别初值？`)) return;
    patchCard((card) => ({
      ...card,
      lines: cloneLines(card.seed),
      text: bodyText(card.seed),
      name_runs: cloneLine(card.name_seed),
      name: lineText(card.name_seed),
      hp: mode === "role" ? card.hp_seed : card.hp,
    }));
    setSelection(null);
    setMessage(`已还原「${active.name}」`);
  };

  const selectedLine =
    active && selection
      ? selection.target === NAME_TARGET
        ? active.name_runs
        : (active.lines[Number(selection.target.slice(5))] ?? null)
      : null;
  const selectedText = selectedLine && selection
    ? lineText(selectedLine).slice(Math.min(selection.start, selection.end), Math.max(selection.start, selection.end))
    : "";
  const selectionLabel = selection
    ? selection.target === NAME_TARGET
      ? "名称"
      : `正文第 ${Number(selection.target.slice(5)) + 1} 行`
    : "";

  const palette = COLOR_PRESETS.map((preset) => (
    <button
      type="button"
      className={`tone-command tone-command-${preset.ink.tone}`}
      onPointerDown={(event) => event.preventDefault()}
      onClick={() => paintSelection(selection, preset.ink, preset.label)}
      key={`${preset.label}-${preset.ink.color ?? preset.ink.tone}`}
    >
      <span
        aria-hidden="true"
        style={preset.ink.color ? { background: preset.ink.color } : undefined}
      />
      {preset.label}
      {preset.shortcut && <kbd>{preset.shortcut}</kbd>}
    </button>
  ));

  const renderInputRef = (target: string) => (node: HTMLInputElement | null): void => {
    if (node) inputRefs.current.set(target, node);
    else inputRefs.current.delete(target);
  };

  const originalLines: ReactNode =
    mode === "role" || !active?.scan.raw.length ? (
      <div className="source-copy">
        <strong>{active?.scan.name || "无名称记录"}</strong>
        <pre>{active?.scan.text || "无识别原稿"}</pre>
      </div>
    ) : (
      <ol className="source-lines">
        {active.scan.raw.map((line, index) => (
          <li key={`${index}-${line}`}>
            <span>{index + 1}</span>
            <b>{line}</b>
          </li>
        ))}
      </ol>
    );

  return (
    <main className="review-app">
      <header className="review-header">
        <div>
          <p className="review-eyebrow">{config.eyebrow}</p>
          <h1>{config.heading}</h1>
          <p>{config.description}</p>
        </div>
        <div className="review-actions">
          <label className="review-button is-import">
            导入审核 JSON
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                if (file) void importFile(file);
              }}
            />
          </label>
          <button type="button" className="review-button" onClick={exportFile} disabled={!sheet}>
            导出校订结果
          </button>
          <button type="button" className="review-button is-quiet" onClick={discardDraft}>
            放弃本机草稿
          </button>
          <a className="review-button is-quiet" href={mode === "role" ? "/card-review" : "/role-review"}>
            {mode === "role" ? "审核普通卡牌" : "审核角色技能"}
          </a>
          <a className="review-button is-quiet" href="/">
            返回牌桌
          </a>
        </div>
      </header>

      <section className="review-progress" aria-label="审核进度">
        <span className="is-pending">待审核 {counts.pending}</span>
        <span className="is-approved">已通过 {counts.approved}</span>
        <span className="is-rejected">需重审 {counts.rejected}</span>
        <span className="is-edited">人工修改 {editedIds.size}</span>
        <strong>{message}</strong>
      </section>

      <div className="review-workspace">
        <aside className="review-catalog">
          <div className="review-filters">
            <input
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder={`搜索${config.itemLabel}名、ID 或正文`}
            />
            <select value={groupFilter} onChange={(event) => setGroupFilter(event.currentTarget.value)}>
              <option value="all">{mode === "role" ? "全部阵营" : "全部卡组"}</option>
              {config.groups.map((group) => (
                <option value={group.value} key={group.value}>{group.label}</option>
              ))}
            </select>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.currentTarget.value)}>
              <option value="all">全部状态</option>
              <option value="pending">待审核</option>
              <option value="approved">已通过</option>
              <option value="rejected">需重审</option>
            </select>
            <label className="review-toggle">
              <input
                type="checkbox"
                checked={changedOnly}
                onChange={(event) => setChangedOnly(event.currentTarget.checked)}
              />
              只看有人工修改的
            </label>
          </div>
          <div className="review-card-list">
            {filtered.map((card) => (
              <button
                type="button"
                className={`review-card-row status-${card.review.status}${card.id === active?.id ? " is-active" : ""}`}
                onClick={() => selectCard(card.id)}
                key={card.id}
              >
                <img
                  src={card.asset}
                  alt=""
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.hidden = true;
                  }}
                />
                <span>
                  <strong>{card.name}</strong>
                  <small>
                    {GROUP_LABEL[reviewGroup(card, mode)] ?? "未分类"} · {STATUS_LABEL[card.review.status]}
                    {mode === "role" && card.hp ? ` · HP ${card.hp}` : ""}
                    {editedIds.has(card.id) ? " · 已修改" : ""}
                  </small>
                  <code>{card.id}</code>
                </span>
              </button>
            ))}
            {filtered.length === 0 && <p className="review-empty">没有符合筛选条件的{config.itemLabel}。</p>}
          </div>
        </aside>

        <section className="review-reference" aria-label={`${config.itemLabel}卡面与识别原稿`}>
          {active ? (
            <>
              <header className="reference-heading">
                <div>
                  <span>
                    {GROUP_LABEL[reviewGroup(active, mode)] ?? "未分类"}
                    {mode === "role" && active.hp ? ` · HP ${active.hp}` : ""}
                  </span>
                  <h2>{active.name}</h2>
                  <code>{active.id}</code>
                </div>
                <button type="button" onClick={() => setImageZoomed((current) => !current)}>
                  {imageZoomed ? "适应窗口" : "放大原图"}
                </button>
              </header>

              <div className={`card-reference-frame${imageZoomed ? " is-zoomed" : ""}`}>
                <img src={active.asset} alt={`${active.name}实体卡面`} draggable={false} />
              </div>

              <article className="source-panel">
                <header>
                  <strong>OCR 识别原稿</strong>
                  <span>{mode === "role" ? "可能有错字与错误断句" : active.scan.raw.length ? `${active.scan.raw.length} 行，含卡组页眉` : "无逐行 OCR"}</span>
                </header>
                {originalLines}
                {mode === "role" && active.scan.raw.length > 0 && (
                  <details className="source-raw">
                    <summary>查看原始逐行 OCR（仅辅助定位）</summary>
                    <ol className="source-lines">
                      {active.scan.raw.map((line, index) => (
                        <li key={`${index}-${line}`}>
                          <span>{index + 1}</span>
                          <b>{line}</b>
                        </li>
                      ))}
                    </ol>
                  </details>
                )}
              </article>

              <article className="source-panel is-core">
                <header>
                  <strong>{mode === "role" ? "当前后端登记" : "当前游戏文本（Core）"}</strong>
                  <span>{mode === "role" ? "只作差异定位，审核后再修逻辑" : "仅作错误对照"}</span>
                </header>
                {mode === "role" ? (
                  <BackendSummary value={active.backend} />
                ) : (
                  <div className="source-copy">
                    <strong>{active.scan.name || "无"}</strong>
                    <pre>{active.scan.text || "无"}</pre>
                  </div>
                )}
              </article>

              <details className="source-meta">
                <summary>素材来源与识别依据</summary>
                <dl>
                  <dt>卡面</dt>
                  <dd>{active.asset}</dd>
                  <dt>来源</dt>
                  <dd>{active.source || "未记录"}</dd>
                  <dt>依据</dt>
                  <dd>{active.evidence || "未记录"}</dd>
                  {mode === "role" ? (
                    <>
                      <dt>阵营</dt>
                      <dd>{GROUP_LABEL[active.camp] ?? active.camp}</dd>
                      <dt>HP</dt>
                      <dd>{active.hp ?? "待确认"}</dd>
                    </>
                  ) : (
                    <>
                      <dt>张数</dt>
                      <dd>{active.copies}</dd>
                      <dt>类别</dt>
                      <dd>{CATEGORY_LABEL[active.category] ?? "未分类"}</dd>
                    </>
                  )}
                </dl>
              </details>
            </>
          ) : (
            <p className="review-empty">请先导入审核 JSON。</p>
          )}
        </section>

        <section className="review-editor" aria-label="校订编辑器">
          {active ? (
            <>
              <header className="editor-heading">
                <div>
                  <span className="editor-kicker">校订对象</span>
                  <h2>{active.id}</h2>
                </div>
                <div className="review-status-picker" role="group" aria-label="审核状态">
                  {(["pending", "approved", "rejected"] as const).map((status) => (
                    <button
                      type="button"
                      className={active.review.status === status ? `is-active status-${status}` : ""}
                      onClick={() => setReviewStatus(status)}
                      key={status}
                    >
                      {STATUS_LABEL[status]}
                    </button>
                  ))}
                </div>
              </header>
              {mode === "role" && (
                <label className="role-hp-editor">
                  <span>
                    <strong>实体卡 HP</strong>
                    <small>照右侧圆形 HP 标记填写；此数值将用于后端角色上限。</small>
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={99}
                    required
                    value={active.hp ?? ""}
                    aria-label="实体卡 HP"
                    onChange={(event) => {
                      const hp = event.currentTarget.value
                        ? positiveInteger(event.currentTarget.value)
                        : null;
                      patchCard((card) => ({ ...card, hp }));
                    }}
                  />
                </label>
              )}


              <div className="tone-toolbar">
                <div>
                  <strong>给选中文字标色</strong>
                  <span>可跨原有色段精确选字；黑／黄／红快捷键为 Ctrl/⌘ + 1／2／3</span>
                </div>
                <div className="tone-commands">
                  {palette}
                  <label className="custom-color">
                    <input
                      type="color"
                      value={customColor}
                      aria-label="选择自定义文字颜色"
                      onChange={(event) => setCustomColor(event.currentTarget.value)}
                    />
                    <code>{customColor.toUpperCase()}</code>
                  </label>
                  <button
                    type="button"
                    className="tone-command tone-command-custom"
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={() =>
                      paintSelection(
                        selection,
                        { tone: "custom", color: customColor },
                        `自定义色 ${customColor.toUpperCase()}`,
                      )
                    }
                  >
                    应用自定义色
                  </button>
                </div>
                <output className={selectedText ? "has-selection" : ""}>
                  {selectedText ? `${selectionLabel} · 已选「${selectedText}」` : "尚未选择文字"}
                </output>
              </div>

              <section className="corrected-preview">
                <header>
                  <div>
                    <strong>校订结果</strong>
                    <span>导出时写入 name / text，并保留逐字颜色结构</span>
                  </div>
                  <ToneLegend />
                  <div className="preview-actions">
                    <button type="button" onClick={copyBody}>复制正文</button>
                    <button type="button" onClick={resetActive} disabled={!cardEdited(active, mode)}>还原初值</button>
                  </div>
                </header>
                <div className="corrected-paper">
                  <h3>
                    <ToneText line={active.name_runs} empty="（名称为空）" />
                    {mode === "role" && <span className="corrected-hp">HP {active.hp ?? "?"}</span>}
                  </h3>
                  <div className="corrected-body">
                    {active.lines.map((line, index) => (
                      <p key={index}><ToneText line={line} empty=" " /></p>
                    ))}
                  </div>
                </div>
              </section>

              <section className="transcript-editor is-name">
                <header>
                  <div>
                    <strong>{mode === "role" ? "角色名" : "卡面首行／名称"}</strong>
                    <span>
                      {mode === "role"
                        ? "姓名照卡面核对；异体字、间隔号和全名均按原图保留。"
                        : "命莲寺卡的阵营词通常标为黄色；标点照实体卡修正。"}
                    </span>
                  </div>
                </header>
                <TextLineEditor
                  line={active.name_runs}
                  number="名"
                  label={mode === "role" ? "角色名" : "卡面名称"}
                  inputRef={renderInputRef(NAME_TARGET)}
                  onChange={(value, input) => {
                    patchTarget(NAME_TARGET, (line) => replaceLineText(line, value));
                    rememberSelection(NAME_TARGET, input);
                  }}
                  onSelect={(input) => rememberSelection(NAME_TARGET, input)}
                  onKeyDown={(event) => handleKeyDown(event, NAME_TARGET)}
                />
              </section>

              <section className="transcript-editor">
                <header>
                  <div>
                    <strong>{mode === "role" ? "角色规则逐行校订" : "正文逐行校订"}</strong>
                    <span>
                      一格就是实体卡的一行。Enter 在光标处断行；行首 Backspace 并回上一行。
                      {mode === "role" ? " 胜利条件、通常能力、特殊能力及次数限制都必须完整保留。" : ""}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const index = active.lines.length - 1;
                      patchLines((lines) => insertLine(lines, index));
                      focusLater(bodyTarget(index + 1), 0);
                    }}
                  >
                    ＋ 末尾加行
                  </button>
                </header>
                <div className="transcript-lines">
                  {active.lines.map((line, index) => {
                    const target = bodyTarget(index);
                    return (
                      <TextLineEditor
                        key={index}
                        line={line}
                        number={String(index + 1)}
                        label={`正文第 ${index + 1} 行`}
                        inputRef={renderInputRef(target)}
                        onChange={(value, input) => {
                          patchTarget(target, (current) => replaceLineText(current, value));
                          rememberSelection(target, input);
                        }}
                        onSelect={(input) => rememberSelection(target, input)}
                        onKeyDown={(event) => handleKeyDown(event, target, index)}
                        onInsert={() => {
                          patchLines((lines) => insertLine(lines, index));
                          focusLater(bodyTarget(index + 1), 0);
                        }}
                        onRemove={() => {
                          patchLines((lines) => removeLine(lines, index));
                          const nextIndex = Math.min(index, Math.max(0, active.lines.length - 2));
                          focusLater(bodyTarget(nextIndex), 0);
                        }}
                      />
                    );
                  })}
                </div>
              </section>

              <label className="review-notes">
                <span>审核备注</span>
                <textarea
                  rows={2}
                  value={active.review.notes}
                  placeholder="记录仍无法从卡面确认的字、标点或规则疑问"
                  onChange={(event) => {
                    const notes = event.currentTarget.value;
                    patchCard((card) => ({
                      ...card,
                      review: { ...card.review, notes },
                    }));
                  }}
                />
              </label>

              <footer className="editor-footer">
                <span>
                  {cardEdited(active, mode)
                    ? mode === "role"
                      ? "名称、HP、文字或颜色已修改，尚未导出"
                      : "文字或颜色已修改，尚未导出"
                    : "尚未修改识别初值"}
                </span>
                <button type="button" className="approve-next" onClick={approveAndNext}>
                  通过并审核下一个{mode === "role" ? "角色" : "卡牌"}
                </button>
              </footer>
            </>
          ) : (
            <p className="review-empty">请从左侧选择{config.itemLabel}。</p>
          )}
        </section>
      </div>
    </main>
  );
}

export function CardReviewApp() {
  return <ReviewApp mode="card" />;
}

export function RoleReviewApp() {
  return <ReviewApp mode="role" />;
}
